/**
 * In-run HUD: timer, speedometer, crosshair, interaction prompt, checkpoint
 * flash and the restart hint. Deliberately minimal - this is a race, not a
 * dashboard.
 *
 * The speedometer is the one addition that is not just decoration. It is what
 * makes the vauhti meter legible: the km/h number falls the instant you jump or
 * clip a car, and the bar under it shows how much of the earnable range the
 * current clean streak has banked. Without it the whole momentum system is
 * invisible and reads as the game randomly feeling slow.
 *
 * The thin amber bar under that one is the beer, for the same reason: a boost
 * you cannot see is indistinguishable from the game being inconsistent.
 */

import { formatDelta, formatSpeedKmh, formatSplit, formatTime } from '../utils/format';
import { PLAYER_POSSESSIVE } from '../config/player';
import { RunState } from '../game/GameState';
import type { Split } from '../world/Checkpoint';
import { el, setHidden, setText } from './dom';
import type { ObjectiveView } from './RunObjective';
import { TaskCheckbox } from './TaskCheckbox';
import { splitDelta } from './SplitComparison';

export class HUD {
  readonly root = el('div', 'hud');

  private readonly timer = el('div', 'hud__timer', '00:00.000');
  private readonly splits = el('div', 'hud__splits');
  private readonly splitLabel = el('div', 'hud__split-label');
  private readonly splitTime = el('b');
  private readonly splitDifference = el('span', 'hud__split-delta');
  private readonly speed = el('div', 'hud__speed');
  private readonly speedLabel = el('div', 'hud__speed-label', `${PLAYER_POSSESSIVE} nopeus`);
  private readonly speedValue = el('div', 'hud__speed-value', '0.0 km/h');
  private readonly speedMeter = el('div', 'hud__speed-meter');
  private readonly speedFill = el('i', 'hud__speed-fill');
  private readonly beerMeter = el('div', 'hud__beer');
  private readonly beerFill = el('i', 'hud__beer-fill');
  private readonly alcohol = el('div', 'hud__alcohol');
  private readonly alcoholLabel = el('div', 'hud__speed-label', `${PLAYER_POSSESSIVE} promillet`);
  private readonly alcoholValue = el('div', 'hud__speed-value', '0,00 ‰');
  private readonly crosshair = el('div', 'hud__crosshair');
  private readonly prompt = el('div', 'hud__prompt');
  private readonly footer = el('div', 'hud__footer');
  private readonly checkpointFlash = el('div', 'hud__checkpoint');
  private readonly shout = el('div', 'hud__shout');
  private readonly objective = el('div', 'hud__objective');
  private readonly objectiveMain = el('div', 'hud__objective-main');
  private readonly objectiveMainCheck = new TaskCheckbox();
  private readonly objectiveStep = el('div', 'hud__objective-step');
  private readonly objectiveText = el('span', 'hud__objective-text');
  private readonly objectiveCounter = el('span', 'hud__objective-counter');
  private readonly objectiveCheck = new TaskCheckbox();

  private flashUntil = 0;

  constructor() {
    this.objectiveStep.setAttribute('role', 'status');
    this.objectiveMain.append(this.objectiveMainCheck.root,
      el('span', 'hud__objective-text', 'Tehtävä: Siirry omalle paikallesi'));
    this.objectiveStep.append(this.objectiveCheck.root, this.objectiveText, this.objectiveCounter);
    this.objective.append(this.objectiveMain, this.objectiveStep);
    for (const instruction of [
      'R = UUSI YRITYS',
      'F = TOIMI',
      'E = HUIKKA',
      '1 2 3 = AAMUPALA',
      'ESC = TAUKO JA VALIKKO',
      'F3 = DEBUG',
    ]) {
      this.footer.appendChild(el('span', 'hud__footer-item', instruction));
    }
    this.speedMeter.appendChild(this.speedFill);
    const splitValues = el('div', 'hud__split-values');
    splitValues.append(this.splitTime, this.splitDifference);
    this.splits.append(this.splitLabel, splitValues);
    this.splitDifference.title = 'Ero omaan ennätykseen';
    this.beerMeter.appendChild(this.beerFill);
    this.speed.append(this.speedLabel, this.speedValue, this.speedMeter, this.beerMeter);
    this.alcohol.append(this.alcoholLabel, this.alcoholValue);
    this.root.append(
      this.timer,
      this.splits,
      this.speed,
      this.alcohol,
      this.crosshair,
      this.prompt,
      this.footer,
      this.checkpointFlash,
      this.shout,
      this.objective,
    );
    setHidden(this.prompt, true);
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
  }

  update(params: {
    state: RunState;
    objective: ObjectiveView | null;
    pinComplete: boolean;
    elapsedMs: number;
    prompt: string | null;
    splits: readonly Split[];
    personalBestSplits: readonly Split[];
    nowSeconds: number;
    /** Shouted line to show right now, if any. */
    shout: { text: string; distant: boolean } | null;
    /** Horizontal ground speed in m/s. */
    speed: number;
    /** The vauhti meter as 0..1. */
    momentum: number;
    /** True for a moment after a jump or a collision took vauhti off you. */
    momentumLost: boolean;
    /** The beer boost as 0..1 of its ceiling. */
    beer: number;
    /** Current alcohol level, including successful sips this run. */
    alcoholPromille: number;
  }): void {
    const inRun = params.state === RunState.Running || params.state === RunState.Finished;
    setHidden(this.root, params.state === RunState.Paused);
    setHidden(this.timer, !inRun);
    setHidden(this.speed, params.state !== RunState.Running);
    setHidden(this.alcohol, params.state !== RunState.Running);
    setHidden(this.crosshair, params.state !== RunState.Running);
    const objective = params.objective;
    this.objectiveMainCheck.update(params.pinComplete);
    this.objectiveMain.classList.toggle('hud__objective--complete', params.pinComplete);
    setHidden(this.objective, params.state !== RunState.Running);
    setHidden(this.objectiveStep, !objective || objective.text === 'Siirry omalle paikallesi');
    if (objective) {
      setText(this.objectiveText, objective.text);
      setText(this.objectiveCounter, objective.counter ?? '');
      setHidden(this.objectiveCounter, objective.counter === null);
      this.objectiveStep.classList.toggle('hud__objective--complete', objective.complete);
      this.objectiveStep.style.opacity = String(objective.opacity);
      this.objectiveStep.style.transform = `translateY(${-12 * (1 - objective.opacity)}px)`;
      this.objectiveCheck.update(objective.complete, objective.check);
    }
    // Only while there is a run to press these keys during. The menus have
    // their own instructions now, and the key list under one is just noise.
    setHidden(this.footer, !inRun);

    setText(this.timer, params.elapsedMs < 0
      ? `-${formatTime(Math.ceil(-params.elapsedMs))}` : formatTime(params.elapsedMs));
    this.timer.classList.toggle('hud__timer--finished', params.state === RunState.Finished);

    setText(this.speedValue, formatSpeedKmh(params.speed));
    setText(this.alcoholValue, `${params.alcoholPromille.toFixed(2).replace('.', ',')} ‰`);
    this.speedFill.style.width = `${Math.round(clamp01(params.momentum) * 100)}%`;
    // Full is worth calling out: it is the only state you cannot improve on.
    this.speed.classList.toggle('hud__speed--maxed', params.momentum >= 0.999);
    this.speed.classList.toggle('hud__speed--lost', params.momentumLost);

    // The beer rides above the earned meter, so it gets its own thin bar above
    // it rather than a share of the same one. Hidden at zero: a bar that is
    // empty for most of a run just teaches the eye to ignore that corner.
    const beer = clamp01(params.beer);
    this.beerFill.style.width = `${Math.round(beer * 100)}%`;
    this.beerMeter.classList.toggle('hud__beer--show', beer > 0.001);
    this.speed.classList.toggle('hud__speed--kalja', beer > 0.001);

    if (params.state === RunState.Running && params.splits.length > 0) {
      const last = params.splits[params.splits.length - 1];
      const delta = splitDelta(last, params.personalBestSplits);
      setHidden(this.splits, false);
      setText(this.splitLabel, last.label);
      setText(this.splitTime, formatSplit(last.timeMs));
      setText(this.splitDifference, delta === null ? 'Ei vertailuaikaa' : formatDelta(delta));
      this.splitDifference.classList.toggle('hud__split-delta--ahead', delta !== null && delta < 0);
      this.splitDifference.classList.toggle('hud__split-delta--behind', delta !== null && delta > 0);
    } else {
      setHidden(this.splits, true);
    }

    if (params.prompt && params.state === RunState.Running) {
      setText(this.prompt, `[F] ${params.prompt}`);
      setHidden(this.prompt, false);
    } else {
      setHidden(this.prompt, true);
    }

    const flashing = params.nowSeconds < this.flashUntil;
    this.checkpointFlash.classList.toggle('hud__checkpoint--show', flashing);

    if (params.shout) {
      setText(this.shout, params.shout.text);
      this.shout.classList.toggle('hud__shout--distant', params.shout.distant);
      this.shout.classList.add('hud__shout--show');
    } else {
      this.shout.classList.remove('hud__shout--show');
    }
  }

  flashCheckpoint(label: string, nowSeconds: number): void {
    setText(this.checkpointFlash, label);
    this.flashUntil = nowSeconds + 1.2;
  }

  clearFlash(): void {
    this.flashUntil = 0;
    this.checkpointFlash.classList.remove('hud__checkpoint--show');
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
